import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';
import { prepareHeaders } from '@/state/slices/index.ts';
import { UserFeatureFlag } from '@/models/featureFlag';

export const FeatureFlagAPI = createApi({
  reducerPath: 'featureFlagAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['FeatureFlags'],
  endpoints: (builder) => ({
    // Effective state of every flag for the current user, keyed by camelCased
    // flag key (e.g. `report_autosave` -> `reportAutosave`).
    getFeatureFlags: builder.query<Record<string, boolean>, void>({
      query: () => ({
        url: '/feature-flags/',
        method: 'GET',
      }),
      providesTags: ['FeatureFlags'],
    }),

    getUserFeatureFlags: builder.query<UserFeatureFlag[], void>({
      query: () => ({
        url: '/feature-flags/user/',
        method: 'GET',
      }),
      providesTags: ['FeatureFlags'],
    }),

    toggleUserFeatureFlag: builder.mutation<
      UserFeatureFlag,
      { key: string; enabled: boolean }
    >({
      query: ({ key, enabled }) => ({
        url: `/feature-flags/user/${key}/`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['FeatureFlags'],
      // Flip the switch right away; roll back if the server rejects it.
      async onQueryStarted({ key, enabled }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          FeatureFlagAPI.util.updateQueryData(
            'getUserFeatureFlags',
            undefined,
            (flags) => {
              const flag = flags.find((f) => f.key === key);
              if (flag) flag.enabled = enabled;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetFeatureFlagsQuery,
  useGetUserFeatureFlagsQuery,
  useToggleUserFeatureFlagMutation,
} = FeatureFlagAPI;
